import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Palette, Sparkles, Lock, ArrowRight, ChevronDown, Check } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

const STEPS = [
  {
    n: 1,
    icon: Camera,
    title: "Upload your room or exterior photo",
    body: "Take a fresh photo with your phone, or upload one from your camera roll. Works best on interior walls, ceilings, doors, fences, garage doors and house exteriors.",
  },
  {
    n: 2,
    icon: Palette,
    title: "Design a complete colour scheme",
    body: "Coordinate colours across every surface — walls, ceilings, trims, doors, roof, fence, deck. Start from a designer scheme, or build your own using our curated 390+ colour library.",
  },
  {
    n: 3,
    icon: Sparkles,
    title: "See your space in seconds",
    body: "Our AI renders your photo with the new colour, preserving shadows and textures. Drag the slider to compare before and after.",
  },
  {
    n: 4,
    icon: Lock,
    title: "Love it? Unlock $29.99 AUD",
    body: "Your first preview is free and watermarked. A one-time $29.99 unlocks that project for unlimited colour changes, before/after comparison, and full HD downloads.",
  },
];

const FAQ = [
  {
    q: "How does a project work — is it 1 photo per project?",
    a: "Yes. 1 photo = 1 project. Each uploaded photo (a room, wall, fence, or exterior view) starts its own project with its own free watermarked preview and its own one-time $29.99 AUD unlock. To visualise a different room or angle, just start another project from the 'Start another project' tile on your Projects page.",
  },
  {
    q: "Can I try multiple colours on different surfaces in the same photo?",
    a: "Yes — this is how to build a full colour scheme in v1. Render the walls in your chosen colour first. Then, on the same project, change the surface (e.g. to ceiling, doors or trims), pick a new colour, and hit Render again. Each render is saved in your project's history strip and you can flick between them or compare any two with the before/after slider. (A one-click 'Scheme Mode' that combines them all into a single image is coming in our next release.)",
  },
  {
    q: "Is this a real photo of my home, or just a colour swatch?",
    a: "It's a real AI re-render of your photo. We preserve your existing lighting, shadows, texture and surroundings — only the surface you chose gets repainted.",
  },
  {
    q: "What does the $29.99 AUD unlock?",
    a: "A single project (one uploaded photo). After payment you can try unlimited colour combinations on that photo, compare before/after, and download in full HD with no watermark. A new uploaded photo is a new project and needs its own unlock.",
  },
  {
    q: "How long does a render take?",
    a: "Usually 10–25 seconds. Complex photos with lots of detail can occasionally take up to 60 seconds. You'll see a progress message the whole way.",
  },
  {
    q: "How realistic are the results?",
    a: "Very realistic for walls, ceilings, doors, fences and standard exterior surfaces. Highly textured surfaces (rough brick, weathered timber, stone) sometimes need a second attempt with a different colour saturation.",
  },
  {
    q: "Can I use this for my whole house?",
    a: "Yes — upload one photo per room or exterior view. Each photo is its own project with its own $29.99 unlock. We're working on multi-photo project bundles for a future release.",
  },
  {
    q: "Can I get a paint code (e.g. Dulux Natural White)?",
    a: "Not yet. Our v1 palette is original / generic. Direct brand-name matching (Dulux, Resene, Taubmans) is on our post-launch roadmap. For now, every colour shows its HEX code so any paint shop can match it.",
  },
  {
    q: "What devices does ColourMatch work on?",
    a: "Any modern phone, tablet, or computer — including iPhone, iPad, Android, Mac and PC. Best experience is on a phone where you can take photos directly in-app.",
  },
  {
    q: "Are my photos private?",
    a: "Yes. Your photos are visible only to you, stored securely, and never shared, sold or used to train AI models.",
  },
  {
    q: "Can I get a refund?",
    a: 'See our full refund policy at the link below. The short version: if the renders for your project clearly aren\'t working, contact support within 7 days and we\'ll make it right.',
  },
  {
    q: "How do I contact you if something goes wrong?",
    a: "Use the Contact page (link in the footer). We aim to respond within 1 business day.",
  },
];

export default function HowItWorks() {
  usePageMeta({
    title: "How it works — ColourMatch AI Paint Visualiser",
    description: "How ColourMatch works: upload your own photo, pick a surface and paint colour, and our AI paint visualiser software renders interior and exterior colours in seconds. Built for Australian homeowners.",
    path: "/how-it-works",
  });
  return (
    <div className="bg-brand-bg" data-testid="how-it-works-page">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-5 lg:px-8 pt-12 lg:pt-20 pb-10 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-ink"
        >
          See your home in any colour, in seconds
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
          className="text-base sm:text-lg text-brand-muted mt-4 max-w-2xl mx-auto"
        >
          Photo-realistic colour previews powered by AI. No painters, no test pots, no guesswork.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.16, ease: [0.16, 1, 0.3, 1] }}
          className="mt-7 flex flex-col sm:flex-row gap-3 justify-center"
        >
          <Link to="/visualiser/new" data-testid="hiw-cta-start"
            className="inline-flex items-center justify-center gap-2 bg-brand-navy text-white h-12 px-7 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90 hover:shadow-navy transition-all tap-spring">
            Try it free <ArrowRight size={16} />
          </Link>
          <Link to="/pricing" data-testid="hiw-cta-pricing"
            className="inline-flex items-center justify-center gap-2 bg-white border-2 border-brand-teal text-brand-teal h-12 px-7 rounded-2xl text-sm font-semibold hover:bg-brand-teal/5 transition-all tap-spring">
            See pricing
          </Link>
        </motion.div>
      </section>

      {/* 4 steps */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-10 lg:pb-16">
        <div className="grid sm:grid-cols-2 gap-5 lg:gap-6">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="bg-white rounded-3xl p-6 lg:p-7 shadow-soft"
                data-testid={`hiw-step-${s.n}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-11 h-11 rounded-2xl bg-brand-teal/10 text-brand-teal flex items-center justify-center">
                    <Icon size={20} />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-brand-teal uppercase tracking-widest mb-1">
                      Step {s.n}
                    </div>
                    <div className="text-lg font-semibold text-brand-ink">{s.title}</div>
                    <div className="text-sm text-brand-muted mt-1.5 leading-relaxed">{s.body}</div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Tips */}
      <section className="max-w-4xl mx-auto px-5 lg:px-8 pb-10">
        <div className="bg-brand-teal/5 border border-brand-teal/20 rounded-3xl p-6 lg:p-8">
          <div className="text-xs font-bold text-brand-teal uppercase tracking-widest mb-3">Tips for the best results</div>
          <ul className="space-y-2.5 text-sm text-brand-ink">
            {[
              "Use natural daylight where possible — direct sun creates harsh shadows.",
              "Get the whole surface in frame — full walls work better than close-ups.",
              "Hold the camera steady and level — slight blur reduces realism.",
              "Avoid heavy filters or HDR effects on your phone camera.",
              "For exteriors, capture the wall, fence or garage square-on rather than at a sharp angle.",
            ].map((t, i) => (
              <li key={i} className="flex gap-2.5">
                <Check size={16} className="text-brand-teal flex-shrink-0 mt-0.5" /> <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 lg:px-8 pb-16 lg:pb-24" data-testid="hiw-faq">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-ink mb-6">Frequently asked</h2>
        <div className="space-y-2.5">
          {FAQ.map((item, i) => <FaqItem key={i} idx={i} {...item} />)}
        </div>

        <div className="mt-10 text-center bg-white rounded-3xl p-6 lg:p-8 shadow-soft">
          <p className="text-sm text-brand-muted mb-4">Still have a question?</p>
          <Link to="/contact" data-testid="hiw-contact-link"
            className="inline-flex items-center gap-2 bg-brand-navy text-white h-11 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90 transition-all tap-spring">
            Contact us
          </Link>
        </div>
      </section>
    </div>
  );
}

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`hiw-faq-${idx}`}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-brand-sand/30 transition-colors tap-spring"
      >
        <span className="text-sm sm:text-base font-semibold text-brand-ink">{q}</span>
        <ChevronDown size={18} className={`flex-shrink-0 text-brand-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
          transition={{ duration: 0.25 }}
          className="px-5 pb-4 text-sm text-brand-muted leading-relaxed"
        >
          {a}
        </motion.div>
      )}
    </div>
  );
}
